import { useCallback, useEffect, useState } from "react";
import backendClient, { type ConsulBrowserSession } from "../../../lib/backend";

type BrowserStatus = "starting" | "active" | "closed";

export interface ConsulBrowserArtifactState {
  browserSessionId?: string;
  browserLiveUrl?: string;
  browserStatus: BrowserStatus;
  isBrowserUserControlling: boolean;
  onBrowserTakeControl?: () => void;
  onBrowserResumeAgent?: () => void;
}

export function useConsulBrowserArtifact(): ConsulBrowserArtifactState {
  const [session, setSession] = useState<ConsulBrowserSession | null>(null);
  const [status, setStatus] = useState<BrowserStatus>("closed");
  const [isBrowserUserControlling, setIsBrowserUserControlling] =
    useState(false);

  const loadActiveSession = useCallback(async () => {
    return backendClient.consulBrowser.getActive().catch(() => null);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const run = async () => {
      const active = await loadActiveSession();
      if (!isMounted) return;
      const nextSession = active?.session ?? null;
      setSession(nextSession);
      setStatus(nextSession ? "active" : "closed");
      if (!nextSession) setIsBrowserUserControlling(false);
    };
    void run();
    const interval = window.setInterval(() => void run(), 5_000);
    window.addEventListener("focus", run);
    return () => {
      isMounted = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", run);
    };
  }, [loadActiveSession]);

  const handleTakeControl = useCallback(() => {
    if (!session) return;
    setIsBrowserUserControlling(true);
  }, [session]);

  const handleResumeAgent = useCallback(() => {
    if (!session) return;
    setIsBrowserUserControlling(false);
    void backendClient.consulBrowser.extract().catch(() => undefined);
  }, [session]);

  return {
    browserSessionId: session?.id,
    browserLiveUrl: session?.liveUrl,
    browserStatus: status,
    isBrowserUserControlling,
    onBrowserTakeControl: session ? handleTakeControl : undefined,
    onBrowserResumeAgent: session ? handleResumeAgent : undefined,
  };
}
